import React, { useEffect, useState } from 'react';
import { DataGridIdentifiers } from '../Interface/Datagrid/DataGridIdentifiers';
import { DeleteDialogUI, FileTabUI, FormErrorUI, FormLabelUI, InputUI, TabsUI } from '../Interface';
import ExcelImportTab from '../Interface/ExcelImportTab';
import ItemPrice from './ItemPrice';
import MasterdataClassificationTab from '../MasterdataClassification/MasterdataClassificationTab';
import { MasterDataClassificationType } from '../../config/masterdataClassification';
import jokerService from '../../service/joker.service';
import { JokerTypes } from '../../config/joker';
import JokerTab from '../Interface/Joker/JokerTab';
import { Field, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import ItemOtherPropsTab from './ItemOtherPropsTab';
import ItemAdditionalDataTab from './ItemAdditionalDataTab';
import { FormType } from '../../config/form';
import ItemCategoryItemLinks from './ItemCategoryItemLinks';
import useParam from '../../context/ParamContext';
import TagItemLinks from './TagItemLinks';
import ItemPropertyAssignmentTab from './ItemPropertyAssignmentTab';
import RelatedItemTable from './RelatedItemTable';

const DescriptionPanel = (props) => {
  const { disabled } = props;
  const { values, errors } = useFormikContext();

  return (
    <div className="col-span-full relative">
      <div className="mt-2">
        <Field
          type="text"
          name="description"
          as={InputUI}
          className={`${disabled ? 'rounded-md' : ''} resize-none h-36, p-2`}
          fullWidth
          label={<FormLabelUI text="Leírás" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.description} />}
          disabled={disabled}
          InputLabelProps={{ shrink: values.description !== '' }}
          multiline
          rows={4}
        />
      </div>
    </div>
  );
};

const ItemTabs = (props) => {
  const { params } = useParam();
  const { setFieldValue, values } = useFormikContext();
  const { disabled, jokerRefreshKey } = props;
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialog, setDeletDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [jokerGroups, setJokerGroups] = useState([]);

  const handleUpload = (files) => {
    const filesWithId = files.map((file) => {
      file.id = uuidv4();
      file.active = true;
      return file;
    });

    const existingAttachments = Array.isArray(values.itemAttachment) ? values.itemAttachment : [];

    setFieldValue('itemAttachment', [...existingAttachments, ...filesWithId]);
  };

  const hideDeleteDialog = () => {
    setDeletDialog(false);
  };

  const handleRemoveElement = () => {
    setFieldValue(
      'itemAttachment',
      (values.itemAttachment || []).filter((itemAttachment) => itemAttachment.id !== fileToDelete.id)
    );

    setFileToDelete(null);
    hideDeleteDialog();
  };

  const getJokerGroups = async () => {
    jokerService.getGroups(JokerTypes.ITEM).then((data) => {
      setJokerGroups(data);
    });
  };

  useEffect(() => {
    getJokerGroups();
  }, [jokerRefreshKey]);

  const tabs = [
    {
      id: 0,
      name: 'Pénzügyi adatok',
      content: <ItemOtherPropsTab {...props} />,
    },
    {
      id: 1,
      name: 'További adatok',
      content: <ItemAdditionalDataTab {...props} />,
    },
    {
      id: 2,
      name: 'Megjegyzés',
      content: <DescriptionPanel {...props} />,
    },
    {
      id: 3,
      name: 'Adat import',
      content: <ExcelImportTab {...props} />,
    },
    {
      id: 4,
      name: 'Csatolmányok',
      content: (
        <FileTabUI
          datagridIdentifier={DataGridIdentifiers.uploadFileWithActive}
          savedFiles={values?.itemAttachment ?? []}
          handleDelete={(file) => {
            setFileToDelete(file);
            setDeletDialog(true);
          }}
          disabled={disabled}
          handleUpload={handleUpload}
          form={FormType.ITEM}
        />
      ),
    },
    {
      id: 5,
      name: 'Besorolás',
      content: <MasterdataClassificationTab {...props} type={MasterDataClassificationType.ITEM} />,
    },
    {
      id: 6,
      name: 'Cikkárak',
      content: <ItemPrice {...props} />,
    },
    ...(params?.USE_ITEMPROPERTY === 'Y'
      ? [
          {
            id: 7,
            name: 'Cikk tulajdonságok',
            content: <ItemPropertyAssignmentTab {...props} />,
          },
        ]
      : []),
    ...(params?.SHOW_ITEM_ITEMCATEGORY_TAB === 'Y'
      ? [
          {
            id: params?.USE_ITEMPROPERTY === 'Y' ? 8 : 7,
            name: 'Termékkategóriák',
            content: <ItemCategoryItemLinks {...props} />,
          },
        ]
      : []),
    ...(params?.SHOW_ITEM_TAG_TAB === 'Y'
      ? [
          {
            id: params?.USE_ITEMPROPERTY === 'Y' ? 9 : 8,
            name: 'Címkék és matricák',
            content: <TagItemLinks {...props} />,
          },
        ]
      : []),
    ...(params?.SHOW_ITEM_RELATEDITEM_TAB === 'Y'
      ? [
          {
            id: params?.USE_ITEMPROPERTY === 'Y' ? 10 : 9,
            name: 'Kapcsolódó termékek',
            content: <RelatedItemTable {...props} />,
          },
        ]
      : []),
    ...((jokerGroups.length > 0 &&
      jokerGroups
        .filter((group) => group.jokerFields.length > 0)
        .map((group, index) => ({
          id: index + (params?.USE_ITEMPROPERTY === 'Y' ? 11 : 10),
          name: group.name,
          content: <JokerTab fields={group.jokerFields} key={index} {...props} />,
        }))) ||
      []),
  ];

  const handleTabClick = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  return (
    <div className="my-4 mt-6">
      <div className="px-3">
        <TabsUI activeTab={activeTab} handleTabClick={handleTabClick}>
          {tabs.map((tab, index) => (
            <div key={index} name={tab.name}>
              {tab.content}
            </div>
          ))}
        </TabsUI>
      </div>
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={deleteDialog}
        onHide={() => {
          hideDeleteDialog();
          setFileToDelete(null);
        }}
      />
    </div>
  );
};

export default ItemTabs;
